import {
  ALL_REPORT_SECTIONS,
  buildReport,
  type Entry,
  type ReportSections,
  type Tag,
  type Track,
} from '@anytracker/core'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Button } from '../../components/Button.js'
import { TextField } from '../../components/Field.js'
import { RangeControl } from '../../range/RangeControl.js'
import { useRange } from '../../range/RangeProvider.js'
import { useServices } from '../../services/ServicesProvider.js'
import { ReportDocument } from './ReportDocument.js'
import styles from './ReportScreen.module.css'

const SECTION_LABELS: readonly {
  readonly key: keyof ReportSections
  readonly title: string
  readonly detail: string
}[] = [
  {
    key: 'overlay',
    title: 'Tracks together',
    detail: 'One page comparing the shape of every track that has a scale.',
  },
  {
    key: 'perTrack',
    title: 'A page per track',
    detail: 'Each track over time, and when in the day it was recorded.',
  },
  {
    key: 'appendix',
    title: 'Appendix',
    detail: 'Tag counts, and every note in date order.',
  },
]

/**
 * The report (T-504).
 *
 * Generated entirely on this device — there is no service that turns a document
 * into a PDF here, and adding one would send the data off the device, which is
 * the one thing this app does not do (ADR-0003).
 *
 * The document is laid out as real A4 pages and handed to the browser's own
 * print pipeline, which is what makes the charts vector at whatever resolution
 * the printer wants. An image-based export would be a screenshot of a phone.
 *
 * What is on screen is the document, not a preview of one: the same markup
 * prints, so there is no second layout that could be wrong.
 */
export function ReportScreen() {
  const { repos, clock } = useServices()
  const { resolved, ready } = useRange()

  const [tracks, setTracks] = useState<Track[] | null>(null)
  const [entries, setEntries] = useState<Entry[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [title, setTitle] = useState('')
  const [excludedIds, setExcludedIds] = useState<readonly string[]>([])
  const [sections, setSections] = useState<ReportSections>(ALL_REPORT_SECTIONS)

  const span = useMemo(
    () => ({ from: resolved.fromEpochMs, to: resolved.toEpochMs }),
    [resolved.fromEpochMs, resolved.toEpochMs],
  )

  const reload = useCallback(async () => {
    if (!ready) {
      return
    }
    const [allTracks, found, allTags] = await Promise.all([
      // Archived included: a track retired last month still has history inside
      // this range, and leaving it out puts a hole in the document.
      repos.tracks.list({ includeArchived: true }),
      repos.entries.find({ range: { fromEpochMs: span.from, toEpochMs: span.to } }),
      repos.tags.list(),
    ])
    setTracks(allTracks)
    setEntries(found)
    setTags(allTags)
  }, [repos, ready, span.from, span.to])

  useEffect(() => {
    void reload()
  }, [reload])

  const chosen = useMemo(
    () => (tracks ?? []).filter((track) => !excludedIds.includes(track.id)),
    [tracks, excludedIds],
  )

  const report = useMemo(
    () =>
      buildReport({
        range: resolved,
        tracks: chosen,
        entries,
        tags,
        sections,
        generatedAt: clock.now(),
        ...(title.trim() === '' ? {} : { title: title.trim() }),
      }),
    [resolved, chosen, entries, tags, sections, clock, title],
  )

  return (
    <>
      {/*
        Everything above the document is hidden when printing — see the
        stylesheet. The document below is the artefact; this is the desk it is
        assembled on.
      */}
      <div className={styles['setup']}>
        <h1>Report</h1>
        <p className={styles['intro']}>
          A summary to hand to someone else. Generated on this device — nothing is sent
          anywhere.
        </p>

        <RangeControl empty={tracks !== null && entries.length === 0} />

        <TextField
          label="Title"
          value={title}
          placeholder="anytracker report"
          onChange={(event) => {
            setTitle(event.target.value)
          }}
        />

        <fieldset className={styles['group']}>
          <legend className={styles['legend']}>Include</legend>
          {SECTION_LABELS.map((section) => (
            <label key={section.key} className={styles['option']}>
              <input
                type="checkbox"
                checked={sections[section.key]}
                onChange={() => {
                  setSections({ ...sections, [section.key]: !sections[section.key] })
                }}
              />
              <span className={styles['optionTitle']}>{section.title}</span>
              <span className={styles['optionDetail']}>{section.detail}</span>
            </label>
          ))}
        </fieldset>

        {tracks !== null && tracks.length > 0 && (
          <fieldset className={styles['group']}>
            <legend className={styles['legend']}>Tracks</legend>
            {tracks.map((track) => {
              const included = !excludedIds.includes(track.id)
              return (
                <label key={track.id} className={styles['track']}>
                  <input
                    type="checkbox"
                    checked={included}
                    onChange={() => {
                      setExcludedIds(
                        included
                          ? [...excludedIds, track.id]
                          : excludedIds.filter((id) => id !== track.id),
                      )
                    }}
                  />
                  {track.name}
                  {track.archived && <span className={styles['archived']}>archived</span>}
                </label>
              )
            })}
          </fieldset>
        )}

        <Button
          variant="primary"
          onClick={() => {
            window.print()
          }}
        >
          Print or save as PDF
        </Button>
      </div>

      {tracks === null ? (
        <p>Loading…</p>
      ) : (
        <ReportDocument report={report} entries={entries} deviceZone={clock.timeZone()} />
      )}
    </>
  )
}
